import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography
} from '@mui/material'
import { AxiosError } from 'axios'
import {
  FunctionComponent, useCallback, useEffect, useRef, useState
} from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../../../../api'

interface Props {
  resultSetId: number
  accessToken?: string | null
  // the confirmed site and time of the scan, written by this input only
  value: string | null
  disabled: boolean
  isOffline: boolean
  onConfirmed: (value: string) => void
}

type ScannerControls = { stop: () => void }

// Check code of a site (question type site_code): scan the QR or barcode that hangs in the
// site, or type the short code below it. The server checks the code, closing needs a valid one.
const QuestionSiteCodeInput: FunctionComponent<Props> = ({
  resultSetId, accessToken, value, disabled, isOffline, onConfirmed
}: Props) => {
  const { t, i18n } = useTranslation()
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const video = useRef<HTMLVideoElement | null>(null)
  const controls = useRef<ScannerControls | null>(null)

  const check = useCallback(async (code: string) => {
    if (!code.trim()) return
    if (isOffline || !navigator.onLine) {
      setError(t('siteCodes.fill.needsInternet'))
      return
    }
    setChecking(true)
    setError(null)
    try {
      const scan = await api.siteCodes.scan(resultSetId, code, accessToken)
      if (!scan) {
        setError(t('siteCodes.fill.needsInternet'))
        return
      }
      const at = new Date(scan.scanned_at).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })
      setTyped('')
      onConfirmed(`${scan.name}, ${at}`)
    } catch (e) {
      const status = (e as AxiosError)?.response?.status
      setError(t(status === 429 ? 'siteCodes.fill.tooManyAttempts' : 'siteCodes.fill.invalid'))
    } finally {
      setChecking(false)
    }
  }, [accessToken, i18n.language, isOffline, onConfirmed, resultSetId, t])

  const stopCamera = useCallback(() => {
    controls.current?.stop()
    controls.current = null
  }, [])

  // the reader is only loaded when somebody scans
  useEffect(() => {
    if (!scannerOpen) return undefined
    let cancelled = false
    setCameraError(null)
    // the video element is there once the dialog has rendered
    const start = setTimeout(async () => {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser')
        if (cancelled || !video.current) return
        const reader = new BrowserMultiFormatReader()
        controls.current = await reader.decodeFromConstraints(
          { video: { facingMode: 'environment' } },
          video.current,
          (result) => {
            if (!result) return
            stopCamera()
            setScannerOpen(false)
            check(result.getText())
          }
        )
        if (cancelled) stopCamera()
      } catch {
        if (!cancelled) setCameraError(t('siteCodes.fill.noCamera'))
      }
    }, 50)
    return () => {
      cancelled = true
      clearTimeout(start)
      stopCamera()
    }
  }, [check, scannerOpen, stopCamera, t])

  if (value) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Alert severity="success" sx={{ flex: 1, minWidth: 220 }}>
          {t('siteCodes.fill.confirmed', { value })}
        </Alert>
        {!disabled && (
          <Button size="small" onClick={() => onConfirmed('')}>{t('siteCodes.fill.scanAgain')}</Button>
        )}
      </Box>
    )
  }

  if (disabled) {
    return <Typography color="text.secondary">{t('siteCodes.fill.notScanned')}</Typography>
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Button
        variant="contained"
        size="large"
        onClick={() => setScannerOpen(true)}
        disabled={checking}
        startIcon={<i className="fas fa-qrcode" />}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('siteCodes.fill.scan')}
      </Button>
      <Box
        component="form"
        onSubmit={(e) => { e.preventDefault(); check(typed) }}
        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          label={t('siteCodes.fill.typeCode')}
          value={typed}
          onChange={(e) => setTyped(e.target.value.toUpperCase())}
          inputProps={{ maxLength: 12, autoCapitalize: 'characters', autoComplete: 'off', spellCheck: false }}
          sx={{ width: 180 }}
        />
        <Button type="submit" variant="outlined" disabled={checking || !typed.trim()}>
          {t('siteCodes.fill.check')}
        </Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}

      <Dialog open={scannerOpen} onClose={() => setScannerOpen(false)} fullWidth maxWidth="sm">
        <DialogContent sx={{ p: 1.5 }}>
          {cameraError ? (
            <Alert severity="warning">{cameraError}</Alert>
          ) : (
            <Box
              component="video"
              ref={video}
              muted
              playsInline
              sx={{ width: '100%', borderRadius: 1, bgcolor: '#000', display: 'block' }}
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('siteCodes.fill.scanHint')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScannerOpen(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default QuestionSiteCodeInput
